import { takeLatest, put, call, take, fork, takeEvery, select } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { APPOINTMENT_TASK_TYPES } from 'constants/AppointmentConstants';
import * as appointmentActions from 'modules/appointments/actions/details';
import { FETCH_INTERVENTION_SUCCESS, FETCH_INTERVENTION_FAILURE } from 'modules/appointments/actionTypes';
import { getClientDocumentsTask } from 'modules/appointments/selectors/details';
import { PATCH_ADVICE_SUCCESS } from 'modules/checklists/actionTypes';
import { fetchVault as fetchVaultAction } from 'modules/lockers/actions';
import { fetchVaultWorker } from 'modules/lockers/sagas';
import { createNotification } from 'modules/toast-notifications/actions';

import * as actions from './actions';
import * as actionTypes from './actionTypes';

function* postPrepare(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(
      actions.prepareSuccess(response.data, {
        appointmentTaskId: action.meta.id,
      })
    );
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_preparation.success',
          defaultMessage: 'Preparation completed',
        },
      })
    );
  } catch (error) {
    yield put(actions.prepareFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_preparation.failure',
          defaultMessage: 'Can’t complete the preparation',
        },
      })
    );
  }
}

function* postUnprepare(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(
      actions.unprepareSuccess(response.data, {
        appointmentTaskId: action.meta.id,
      })
    );
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_unpreparation.success',
          defaultMessage: 'Preparation canceled',
        },
      })
    );
  } catch (error) {
    yield put(actions.unprepareFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_unpreparation.failure',
          defaultMessage: 'Can’t cancel the preparation',
        },
      })
    );
  }
}

function* fetchAppointmentTasks(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.fetchLockerAppointmentTasksSuccess(response.data, request.params));
  } catch (error) {
    yield put(actions.fetchLockerAppointmentTasksFailure(error));
  }
}

function* postVerify(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(
      actions.verifySuccess(response.data, {
        appointmentTaskId: action.meta.id,
      })
    );
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_verification.success',
          defaultMessage: 'Verification completed',
        },
      })
    );
  } catch (error) {
    yield put(actions.verifyFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_verification.failure',
          defaultMessage: 'Can’t complete the verification',
        },
      })
    );
  }
}

function* create({ payload }) {
  const { meta, request } = payload;
  const { appointmentId } = meta;

  try {
    const { data: task } = yield call(restClient.request, request);
    yield put(actions.createSuccess(appointmentId, task));
    if (task.vaultId) {
      yield fork(fetchVaultWorker, fetchVaultAction({ id: task.vaultId }));
    }
  } catch (errorMessage) {
    yield put(actions.createFailure(errorMessage));
  }
}

function* reschedule({ payload }) {
  const { meta, request } = payload;
  const { id, appointmentId } = meta;

  try {
    const { data: task } = yield call(restClient.request, request);
    yield put(actions.rescheduleSuccess(appointmentId, task, id));
    if (task.vaultId) {
      yield fork(fetchVaultWorker, fetchVaultAction({ id: task.vaultId }));
    }
  } catch (errorMessage) {
    yield put(actions.rescheduleFailure(errorMessage));
  }
}

function* rescheduleKeyDeposit(action) {
  yield fork(reschedule, action);

  const result = yield take([actionTypes.RESCHEDULE_SUCCESS, actionTypes.RESCHEDULE_FAILURE]);
  const type = result.type === actionTypes.RESCHEDULE_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.RESCHEDULE_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.dropOff.edit.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.RESCHEDULE_SUCCESS
        ? 'Drop-off updated. Previous booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* rescheduleKeyRetrieve(action) {
  yield fork(reschedule, action);

  const result = yield take([actionTypes.RESCHEDULE_SUCCESS, actionTypes.RESCHEDULE_FAILURE]);
  const type = result.type === actionTypes.RESCHEDULE_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.RESCHEDULE_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.pickUp.edit.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.RESCHEDULE_SUCCESS
        ? 'Pick-up updated. Previous booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* update({ payload }) {
  const { meta, request } = payload;
  const { appointmentId } = meta;

  try {
    const { data: task } = yield call(restClient.request, request);
    yield put(actions.updateSuccess(appointmentId, task));
    if (task.vaultId) {
      yield fork(fetchVaultWorker, fetchVaultAction({ id: task.vaultId }));
    }
  } catch (errorMessage) {
    yield put(actions.updateFailure(errorMessage));
  }
}

function* updateKeyDeposit(action) {
  yield fork(update, action);

  const result = yield take([actionTypes.PUT_SUCCESS, actionTypes.PUT_FAILURE]);
  const type = result.type === actionTypes.PUT_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.PUT_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.dropOff.edit.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.PUT_SUCCESS
        ? 'Drop-off updated. Previous booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* updateKeyRetrieve(action) {
  yield fork(update, action);

  const result = yield take([actionTypes.PUT_SUCCESS, actionTypes.PUT_FAILURE]);
  const type = result.type === actionTypes.PUT_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.PUT_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.pickUp.edit.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.PUT_SUCCESS
        ? 'Pick-up updated. Previous booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* deleteSaga({ payload }) {
  const { meta, request } = payload;
  const { id, appointmentId } = meta;

  try {
    yield call(restClient.request, request);
    yield put(actions.deleteTaskSuccess(id, appointmentId));
    yield put(appointmentActions.fetchEvents(appointmentId));
  } catch (errorMessage) {
    yield put(actions.deleteTaskFailure(errorMessage));
  }
}

function* createKeyDeposit(action) {
  yield fork(create, action);

  const result = yield take([actionTypes.POST_SUCCESS, actionTypes.POST_FAILURE]);

  const type = result.type === actionTypes.POST_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.POST_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.dropOff.create.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.POST_SUCCESS ? 'Drop-off planned. Safe booked' : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* createKeyRetrieve(action) {
  yield fork(create, action);

  const result = yield take([actionTypes.POST_SUCCESS, actionTypes.POST_FAILURE]);

  const type = result.type === actionTypes.POST_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.POST_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.pickUp.create.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.POST_SUCCESS ? 'Pick up planned. Safe booked' : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* createRentalVehicleTask(action) {
  yield fork(create, action);

  const result = yield take([actionTypes.POST_SUCCESS, actionTypes.POST_FAILURE]);
  const type = result.type === actionTypes.POST_SUCCESS ? 'success' : 'error';

  if (result.type === actionTypes.POST_SUCCESS) {
    yield put(actions.showAddDriverLicence());
  }

  const message = {
    id:
      result.type === actionTypes.POST_SUCCESS
        ? 'page.keyDeposit.appointments.details.tasks.success'
        : 'http.error.message',
    defaultMessage: result.type === actionTypes.POST_SUCCESS ? 'Appointment updated' : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* createRepairOrderTask({ payload }) {
  const { meta, request } = payload;
  const { appointmentId } = meta;

  try {
    const { data: task } = yield call(restClient.request, request);

    yield put(appointmentActions.fetchIntervention(appointmentId));
    const result = yield take([FETCH_INTERVENTION_SUCCESS, FETCH_INTERVENTION_FAILURE]);

    if (result.type === FETCH_INTERVENTION_FAILURE) {
      throw new Error("Error while fetching the appointment's intervention");
    }

    const message = {
      id: 'page.keyDeposit.appointments.details.tasks.success',
      defaultMessage: 'Appointment updated',
    };
    yield put(createNotification({ type: 'success', message }));

    yield put(actions.createSuccess(appointmentId, task));
  } catch (errorMessage) {
    const message = {
      id: 'http.error.message',
      defaultMessage: 'Oops! Something went wrong.',
    };
    yield put(createNotification({ type: 'error', message }));

    yield put(actions.createFailure(errorMessage));
  }
}

function* createGenericTask(action) {
  yield fork(create, action);

  const result = yield take([actionTypes.POST_SUCCESS, actionTypes.POST_FAILURE]);
  const type = result.type === actionTypes.POST_SUCCESS ? 'success' : 'error';
  const message = {
    id:
      result.type === actionTypes.POST_SUCCESS
        ? 'page.keyDeposit.appointments.details.tasks.success'
        : 'http.error.message',
    defaultMessage: result.type === actionTypes.POST_SUCCESS ? 'Appointment updated' : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* deleteKeyDeposit(action) {
  yield fork(deleteSaga, action);

  const result = yield take([actionTypes.DELETE_SUCCESS, actionTypes.DELETE_FAILURE]);
  const type = result.type === actionTypes.DELETE_SUCCESS ? 'success' : 'error';

  const message = {
    id:
      result.type === actionTypes.DELETE_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.dropOff.cancel.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.DELETE_SUCCESS
        ? 'Drop-off canceled. Safe booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* deleteKeyRetrieve(action) {
  yield fork(deleteSaga, action);

  const result = yield take([actionTypes.DELETE_SUCCESS, actionTypes.DELETE_FAILURE]);
  const type = result.type === actionTypes.DELETE_SUCCESS ? 'success' : 'error';

  const message = {
    id:
      result.type === actionTypes.DELETE_SUCCESS
        ? 'page.keyDeposit.appointments.bookingModal.pickUp.cancel.success'
        : 'http.error.message',
    defaultMessage:
      result.type === actionTypes.DELETE_SUCCESS
        ? 'Pick-up canceled. Safe booking canceled'
        : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* deleteGenericTask(action) {
  yield fork(deleteSaga, action);

  const result = yield take([actionTypes.DELETE_SUCCESS, actionTypes.DELETE_FAILURE]);
  const type = result.type === actionTypes.DELETE_SUCCESS ? 'success' : 'error';

  const message = {
    id:
      result.type === actionTypes.DELETE_SUCCESS
        ? 'page.keyDeposit.appointments.details.tasks.success'
        : 'http.error.message',
    defaultMessage: result.type === actionTypes.DELETE_SUCCESS ? 'Appointment updated' : 'Oops! Something went wrong.',
  };

  yield put(createNotification({ type, message }));
}

function* deleteMedia({ payload }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteMediaSuccess(request.data.appointmentTaskId, request.data.mediaId));
  } catch (error) {
    yield put(actions.deleteMediaFailure(error));
  }
}

function* resolveIssue({ payload }) {
  const { request } = payload;
  let notification = {
    type: 'success',
    message: {
      id: 'page.keyDeposit.appointments.details.management.safeBookings.resolveSuccess',
      defaultMessage: 'Key problem manually solved',
    },
  };

  try {
    const response = yield call(restClient.request, request);
    yield put(actions.postResolveIssueSuccess({ attentionPoints: [], ...response.data }));
  } catch (error) {
    yield put(actions.postResolveIssueFailure(error));
    notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
  }
  yield put(createNotification(notification));
}

function* acknowledgeIssue({ payload }) {
  const { request, appointmentId } = payload;
  let notification = {
    type: 'success',
    message: {
      id: 'page.keyDeposit.appointments.details.management.rentalVehicle.acknowledgmentSuccess',
      defaultMessage: 'Rental vehicle issue acknowledged',
    },
  };

  try {
    const response = yield call(restClient.request, request);
    yield put(actions.acknowledgeIssueSuccess(response.data));
    yield put(appointmentActions.fetchEvents(appointmentId));
  } catch (error) {
    yield put(actions.acknowledgeIssueFailure(error));
    notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
  }
  yield put(createNotification(notification));
}

function* fetchAppointmentTask({ payload }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.fetchAppointmentTaskSuccess(response.data));
  } catch (error) {
    yield put(actions.fetchAppointmentTaskFailure(error));
  }
}

function* afterDeleteMedia({ payload }) {
  const { appointmentTaskId } = payload;
  yield put(actions.fetchAppointmentTask(appointmentTaskId));
}

function* addDriverLicence({ payload }) {
  const { appointmentId } = payload;

  const clientDocumentsTask = yield select(getClientDocumentsTask);
  if (!clientDocumentsTask) {
    const task = { type: APPOINTMENT_TASK_TYPES.DRIVER_DOCUMENTS };
    yield put(actions.createClientDocument(appointmentId, { task }));
    yield take([actionTypes.POST_SUCCESS, actionTypes.POST_FAILURE]);
  }

  yield put(actions.hideAddDriverLicence());
}

function* patchDriverDocument({ payload }) {
  const { appointmentTask, status } = payload;
  if (appointmentTask) {
    const notification = {
      type: 'success',
      message: {
        id: `page.keyDeposit.appointments.details.management.clientDocuments.notification.${status.toLowerCase()}`,
        defaultMessage: `Client document ${status.toLowerCase()}`,
      },
    };
    yield put(appointmentActions.fetchEvents(appointmentTask.appointmentId));
    yield put(createNotification(notification));
  }
}

export function* appointmentTaskSagas() {
  yield takeLatest(actionTypes.FETCH_LOCKER_TASKS, fetchAppointmentTasks);
  yield takeLatest(actionTypes.POST_REQUEST, create);
  yield takeLatest(actionTypes.POST_VERIFY, postVerify);
  yield takeLatest(actionTypes.POST_PREPARE, postPrepare);
  yield takeLatest(actionTypes.POST_UNPREPARE, postUnprepare);
  yield takeLatest(actionTypes.POST_RESOLVE_ISSUE_REQUEST, resolveIssue);
  yield takeLatest(actionTypes.POST_KEY_DEPOSIT_REQUEST, createKeyDeposit);
  yield takeLatest(actionTypes.POST_KEY_RETRIEVE_REQUEST, createKeyRetrieve);
  yield takeLatest(actionTypes.POST_BILLING_TASK_REQUEST, createGenericTask);
  yield takeLatest(actionTypes.POST_REPAIR_ORDER_REQUEST, createRepairOrderTask);
  yield takeLatest(actionTypes.POST_CLIENT_DOCUMENT_REQUEST, createGenericTask);
  yield takeLatest(actionTypes.POST_RENTAL_VEHICLE_TASK_REQUEST, createRentalVehicleTask);

  yield takeLatest(actionTypes.RESCHEDULE_REQUEST, reschedule);
  yield takeLatest(actionTypes.RESCHEDULE_KEY_DEPOSIT_REQUEST, rescheduleKeyDeposit);
  yield takeLatest(actionTypes.RESCHEDULE_KEY_RETRIEVE_REQUEST, rescheduleKeyRetrieve);

  yield takeLatest(actionTypes.PUT_REQUEST, update);
  yield takeLatest(actionTypes.PUT_KEY_DEPOSIT_REQUEST, updateKeyDeposit);
  yield takeLatest(actionTypes.PUT_KEY_RETRIEVE_REQUEST, updateKeyRetrieve);

  yield takeEvery(actionTypes.DELETE_MEDIA, deleteMedia);
  yield takeLatest(actionTypes.DELETE_REQUEST, deleteSaga);
  yield takeLatest(actionTypes.DELETE_BILLING_REQUEST, deleteGenericTask);
  yield takeLatest(actionTypes.DELETE_KEY_DEPOSIT_REQUEST, deleteKeyDeposit);
  yield takeLatest(actionTypes.DELETE_KEY_RETRIEVE_REQUEST, deleteKeyRetrieve);
  yield takeLatest(actionTypes.DELETE_REPAIR_ORDER_REQUEST, deleteGenericTask);
  yield takeLatest(actionTypes.DELETE_CLIENT_DOCUMENT_REQUEST, deleteGenericTask);
  yield takeLatest(actionTypes.DELETE_RENTAL_VEHICLE_TASK_REQUEST, deleteGenericTask);

  yield takeLatest(actionTypes.FETCH_REQUEST, fetchAppointmentTask);
  yield takeLatest(actionTypes.DELETE_MEDIA_SUCCESS, afterDeleteMedia);

  yield takeLatest(actionTypes.ACKNOWLEDGE_ISSUE, acknowledgeIssue);

  yield takeLatest(actionTypes.ADD_DRIVER_LICENCE, addDriverLicence);
  yield takeLatest(PATCH_ADVICE_SUCCESS, patchDriverDocument);
}
